import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import LangToggle from "@/components/LangToggle";

const BG = "/login-ascend.png";

export default function Login() {
  const { user, login, register, error, errorStatus } = useAuth();
  const branding = useBranding();
  const t = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(() =>
    (params.get("mode") || "").toLowerCase() === "register" ? "register" : "login"
  );
  const [email, setEmail] = useState(() => params.get("email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [signupCode, setSignupCode] = useState(() => params.get("code") || "");
  // Default to 'create' (new company) when prefilled from invitation, since the
  // supplier code only unlocks creating a new company. Stays 'create' otherwise.
  const [joinMode, setJoinMode] = useState("create"); // 'create' | 'join'
  const [busy, setBusy] = useState(false);
  // SEND-191 ITEM 2 — reveal state only. It never leaves this component.
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    if (user) nav(loc.state?.from?.pathname || "/", { replace: true });
  }, [user, nav, loc]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    let ok;
    if (mode === "login") {
      ok = await login(email, password);
    } else if (joinMode === "create") {
      ok = await register(email, password, name, companyName, undefined, signupCode);
    } else {
      ok = await register(email, password, name, undefined, inviteCode, undefined);
    }
    setBusy(false);
    if (ok) toast.success(mode === "login" ? t("common.welcomeBack") : t("common.accountCreated"));
  };

  const logoUrl = branding.supplier_logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.supplier_logo_url}`
    : null;

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--surface)]">
      <div className="absolute top-4 right-4 z-10">
        <LangToggle />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm" data-testid="auth-card">
          <div className="flex items-center gap-3 mb-10">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={branding.supplier_name}
                className="w-11 h-11 object-contain bg-[var(--bar-bg)]"
                data-testid="supplier-logo"
              />
            ) : (
              <div className="w-11 h-11 bg-[var(--bar-bg)] text-[var(--brand)] flex items-center justify-center font-heading text-xl">
                {(branding.supplier_name || "A").charAt(0)}
              </div>
            )}
            <div>
              <div className="font-heading text-xl text-[var(--ink)] leading-none" data-testid="supplier-name">
                {branding.supplier_name}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] mt-1">
                {t("auth.sidingEstimator")}
              </div>
            </div>
          </div>

          <div className="mb-7">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-2">
              {mode === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[var(--ink)] leading-tight">
              {mode === "login" ? t("auth.signIn") : t("auth.freeTagline")}
            </h1>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="label">{t("auth.yourName")}</label>
                  <input
                    className="input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="name-input"
                  />
                </div>

                <div className="border border-[var(--border)] p-3">
                  <div className="flex border-b border-[var(--border)] mb-3">
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs uppercase tracking-wider font-bold ${joinMode === "create" ? "text-[var(--brand-text)] border-b-2 border-[var(--brand)]" : "text-[var(--muted)]"}`}
                      onClick={() => setJoinMode("create")}
                      data-testid="mode-create-co"
                    >
                      {t("auth.newCompany")}
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 text-xs uppercase tracking-wider font-bold ${joinMode === "join" ? "text-[var(--brand-text)] border-b-2 border-[var(--brand)]" : "text-[var(--muted)]"}`}
                      onClick={() => setJoinMode("join")}
                      data-testid="mode-join-co"
                    >
                      {t("auth.joinTeammate")}
                    </button>
                  </div>
                  {joinMode === "create" ? (
                    <>
                      <div className="mb-3">
                        <label className="label">{t("auth.yourCompanyName")}</label>
                        <input
                          className="input"
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder={t("auth.companyNamePlaceholder")}
                          required
                          data-testid="company-name-input"
                        />
                      </div>
                      <div>
                        <label className="label">{t("auth.accessCodeFrom", { supplier: branding.supplier_name })}</label>
                        <input
                          className="input font-mono-num uppercase tracking-wider"
                          type="text"
                          value={signupCode}
                          onChange={(e) => setSignupCode(e.target.value.toUpperCase())}
                          placeholder={t("auth.accessCodePlaceholder")}
                          required
                          data-testid="signup-code-input"
                        />
                        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-1">
                          {branding.supplier_tagline}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="label">{t("auth.teammateInviteCode")}</label>
                      <input
                        className="input font-mono-num uppercase tracking-wider"
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        placeholder={t("auth.invitePlaceholder")}
                        required={joinMode === "join"}
                        data-testid="invite-code-input"
                      />
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-1">
                        {t("auth.inviteHelp")}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="label">{t("auth.email")}</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                name="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="label">{t("auth.password")}</label>
              {/* SEND-191 ITEMS 1 AND 2 (Howard ruled 2026-08-14).
                  ITEM 1 — WHAT THE FIELD DECLARES. Measured in a real
                  engine's DOM (`memory/send191_login_field_probe.json`):
                  `autocapitalize`, `autocorrect`, `spellcheck` and `name`
                  were ALL ABSENT here. `type="password"` already stops iOS
                  capitalising and correcting, and the measurement proved
                  the first-character transformation is a NO-OP against the
                  stored hash — so this is hardening, NOT the cause of the
                  five failures, and it is not reported as one. `name` is
                  the one with real consequence: keychains use name and id
                  heuristics as well as `autocomplete`, and a fill that is
                  unpredictable is the remaining live candidate.
                  ITEM 2 — THE REVEAL. Nine characters with a symbol, typed
                  blind on a phone keyboard, is the condition that produced
                  this. The toggle ends the class. IT IS UI ONLY: no auth
                  logic, no request change, and the value is never logged,
                  stored, copied or sent anywhere it was not already. */}
              <div className="relative">
                <input
                  className="input pr-16"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  name="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] hover:text-[var(--ink)]"
                  aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                  aria-pressed={showPassword}
                  title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                  data-testid="password-reveal-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* SEND-191 ITEM 3 — A LOCKOUT IS NOT A WRONG PASSWORD, AND IT
                MAY NOT LOOK LIKE ONE. Five failures in fifteen minutes on
                one IP returns 429 with the server's own wait; it used to
                land in the same grey box as "Invalid email or password".
                The server's sentence is printed verbatim — the wording and
                the countdown are the route's, never composed here. */}
            {error && errorStatus === 429 && (
              <div className="text-sm border-2 border-[var(--danger)] bg-[var(--danger-soft)] px-3 py-2" data-testid="auth-lockout">
                <div className="font-bold uppercase tracking-wider text-[var(--danger)] mb-1">
                  {t("auth.lockedOut")}
                </div>
                <div className="text-[var(--ink)]">{error}</div>
                <div className="text-xs text-[var(--ink-2)] mt-1">{t("auth.lockedOutHint")}</div>
              </div>
            )}
            {error && errorStatus !== 429 && (
              <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/30 bg-[var(--danger-soft)] px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={busy}
              data-testid="auth-submit-btn"
            >
              {busy ? t("common.pleaseWait") : mode === "login" ? t("auth.signInBtn") : t("auth.createBtn")}
            </button>
          </form>

          <div className="text-sm text-[var(--ink-2)] mt-6">
            {mode === "login" ? (
              <>
                {t("auth.needAccount")}{" "}
                <button
                  className="font-semibold text-[var(--ink)] underline underline-offset-4 decoration-[var(--brand)]"
                  onClick={() => setMode("register")}
                  data-testid="switch-register"
                >
                  {t("auth.register")}
                </button>
              </>
            ) : (
              <>
                {t("auth.alreadyHaveOne")}{" "}
                <button
                  className="font-semibold text-[var(--ink)] underline underline-offset-4 decoration-[var(--brand)]"
                  onClick={() => setMode("login")}
                  data-testid="switch-login"
                >
                  {t("auth.signInBtn")}
                </button>
              </>
            )}
          </div>

          <div className="mt-10 text-[11px] text-[var(--muted)] uppercase tracking-widest">
            {t("auth.providedBy", { supplier: branding.supplier_name })}
          </div>
          <div className="mt-4 text-[10px] text-[var(--muted)] flex items-center gap-3">
            <span>© {new Date().getFullYear()} Pro-Quote Estimating Tool</span>
            <span aria-hidden="true">·</span>
            <a href="/terms" className="hover:text-[var(--ink)]" data-testid="login-terms-link">Terms</a>
            <span aria-hidden="true">·</span>
            <a href="/privacy" className="hover:text-[var(--ink)]" data-testid="login-privacy-link">Privacy</a>
          </div>
        </div>
      </div>
      <div
        className="hidden md:block bg-contain bg-center bg-no-repeat bg-[var(--surface)]"
        style={{ backgroundImage: `url(${BG})` }}
        aria-hidden="true"
      />
    </div>
  );
}
